import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "../../lib/utils";

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;
export const DialogPortal = DialogPrimitive.Portal;

type OverlayProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Overlay
> & {
  ref?: React.Ref<React.ComponentRef<typeof DialogPrimitive.Overlay>>;
};

export function DialogOverlay({ className, ref, ...props }: OverlayProps) {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        "fixed inset-0 z-50 bg-on-surface/40 backdrop-blur-sm data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0",
        className,
      )}
      {...props}
    />
  );
}

type ContentProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> & {
  ref?: React.Ref<React.ComponentRef<typeof DialogPrimitive.Content>>;
};

export function DialogContent({
  className,
  children,
  ref,
  ...props
}: ContentProps) {
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          // `w-[calc(100%-2rem)]` keeps a 1rem gutter on each side so the modal
          // never touches the screen edges on small viewports; `max-w-lg` still
          // caps it on larger ones.
          "fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-md bg-surface-container-lowest p-6 shadow-lg",
          className,
        )}
        {...props}
      >
        {children}
        <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm p-1 text-on-surface-variant transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-primary disabled:pointer-events-none">
          <X className="size-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

export function DialogHeader({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("flex flex-col space-y-1.5 text-left", className)}
      {...props}
    />
  );
}

export function DialogFooter({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("mt-6 flex flex-row justify-end space-x-2", className)}
      {...props}
    />
  );
}

type TitleProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Title
> & {
  ref?: React.Ref<React.ComponentRef<typeof DialogPrimitive.Title>>;
};

export function DialogTitle({ className, ref, ...props }: TitleProps) {
  return (
    <DialogPrimitive.Title
      ref={ref}
      className={cn("text-headline-md leading-none", className)}
      {...props}
    />
  );
}

type DescriptionProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Description
> & {
  ref?: React.Ref<React.ComponentRef<typeof DialogPrimitive.Description>>;
};

export function DialogDescription({
  className,
  ref,
  ...props
}: DescriptionProps) {
  return (
    <DialogPrimitive.Description
      ref={ref}
      className={cn("text-body-md text-on-surface-variant", className)}
      {...props}
    />
  );
}
